import React, { useRef, useState } from 'react';
import { lightTheme } from '../Constants/Colors';
import { body } from '../Constants/Fonts';

// ─────────────────────────────────────────────────────────────
// CommonSearchBar — the shared search / filter input.
//
// Light, token-styled (Colors.js `lightTheme` + Fonts.js `body`) so it
// matches the app surface and sits at the SAME 38px height as
// CommonDropdown — the two are designed to line up side-by-side in a
// filter row.
//
// Features:
//   • leading magnifier icon
//   • clear (✕) button that appears once there's a value
//   • focus ring (primary border) matching the other inputs
//   • Enter-to-submit hook
//
// Props:
//   value        current text (controlled)
//   onChange     (value) => void  — receives the raw string, NOT the event
//   placeholder  default "Search…"
//   disabled     boolean
//   ariaLabel    accessible name for the input
//   minWidth     wrapper min width (px, default 200)
//   showClear    show the clear button when non-empty (default true)
//   onEnter      (value) => void — fired on Enter key
//   autoFocus    focus on mount
//   id, name     passthrough to the <input>
//   style        passthrough on the wrapper
//   inputStyle   passthrough on the <input>
// ─────────────────────────────────────────────────────────────

export default function CommonSearchBar({
    value = '',
    onChange,
    placeholder = 'Search…',
    disabled = false,
    ariaLabel,
    minWidth = 200,
    showClear = true,
    onEnter,
    autoFocus = false,
    id,
    name,
    style,
    inputStyle,
}) {
    const inputRef = useRef(null);
    const [focused, setFocused] = useState(false);
    const hasValue = value != null && value !== '';

    const wrap = {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        minWidth,
        width: '100%',
        ...style,
    };

    const input = {
        ...body(),
        width: '100%',
        height: 38,
        minHeight: 38,
        boxSizing: 'border-box',
        // room for the leading icon (left) and the clear button (right)
        padding: '6px 32px 6px 34px',
        background: lightTheme.surface,
        border: `1px solid ${focused ? lightTheme.primary : lightTheme.borderStrong}`,
        borderRadius: lightTheme.radiusSm,
        color: lightTheme.textPrimary,
        outline: 'none',
        transition: 'border-color 0.15s ease',
        opacity: disabled ? 0.6 : 1,
        cursor: disabled ? 'not-allowed' : 'text',
        ...inputStyle,
    };

    return (
        <div style={wrap}>
            {/* leading magnifier */}
            <svg
                width="15" height="15" viewBox="0 0 24 24" fill="none"
                stroke={lightTheme.textMuted} strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round"
                style={{ position: 'absolute', left: 11, pointerEvents: 'none' }}
                aria-hidden="true"
            >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3" />
            </svg>

            <input
                ref={inputRef}
                id={id}
                name={name}
                type="text"
                value={value}
                placeholder={placeholder}
                disabled={disabled}
                autoFocus={autoFocus}
                aria-label={ariaLabel || placeholder}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                onChange={(e) => onChange && onChange(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && onEnter) onEnter(value); }}
                style={input}
            />

            {/* clear button — only when there's something to clear */}
            {showClear && hasValue && !disabled && (
                <button
                    type="button"
                    aria-label="Clear search"
                    onClick={() => {
                        if (onChange) onChange('');
                        if (inputRef.current) inputRef.current.focus();
                    }}
                    style={{
                        position: 'absolute',
                        right: 8,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 18,
                        height: 18,
                        padding: 0,
                        border: 'none',
                        borderRadius: lightTheme.radiusPill,
                        background: lightTheme.surfaceMuted,
                        color: lightTheme.textMuted,
                        cursor: 'pointer',
                        lineHeight: 1,
                    }}
                >
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 6 6 18" /><path d="m6 6 12 12" />
                    </svg>
                </button>
            )}
        </div>
    );
}
